import {
  Button,
  ButtonIcon,
  CardWrapperWithHeader,
  formatUnixTime,
  Input,
  Modal,
  TrashIcon,
} from '@rabbit/elements/shared-components';
import { useTranslation } from 'react-i18next';
import {
  DocumentIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
} from '@heroicons/react/24/solid';
import { useEffect, useState, useContext } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  PersonaTypeSingleLetter,
  PrincipalsFieldName,
} from '@rabbit/data/types';
import {
  DTNote,
  NoteCategoryType,
  NoteStatus,
  NoteEntityType,
  CommunicationDirection,
  InteractionChannel,
  NoteType,
  InteractionParty,
  InteractionMetadata,
} from '@rabbit/data/types';
import { CreateTable } from '@rabbit/sage/utils/CreateTable.tsx';
import { useGetNotes } from '@rabbit/bizproc/react';
import { nestApiCreateNote, nestApiDeleteNote, nestApiUpdateNote } from '@rabbit/bizproc/core';
import { toast } from 'react-toastify';
import { getPersonaData, titleCase } from '@rabbit/sage/utils/helpers';
import { getRootPersonaFromLexicon } from '@rabbit/bizproc/client';
import { useIdentity } from '@rabbit/data/portal';
import { UserContext } from '../../../context/UserContext';

interface NotesDetailsInterface {
  entityId: string;
  entityType: NoteEntityType;
}

// const priorityOptions = Object.values(Priority).map(value => ({
//   label: value.charAt(0).toUpperCase() + value.slice(1),
//   value: value,
// }));

const categoryOptions = Object.values(NoteCategoryType).map((value) => ({
  label: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
  value: value,
}));

const noteTypeOptions = [
  { label: 'General Note', value: 'general' },
  { label: 'Call Log', value: 'call' },
  { label: 'Email Log', value: 'email' },
  { label: 'Customer Interaction', value: 'customer' },
];

const validationSchema = Yup.object().shape({
  title: Yup.string().trim().required('Please enter a title'),
  content: Yup.string().trim().required('Please enter a description'),
  category: Yup.string().required('Please select a category'),
});

export default function NotesDetailContentSection({
  entityType,
  entityId,
}: NotesDetailsInterface) {
  const contextValues = useContext(UserContext);
  const [viewNote, setViewNote] = useState<DTNote | null>(null);
  const [editNote, setEditNote] = useState<DTNote | null>(null);
  const [addNotes, setAddNotes] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const { t } = useTranslation();

  const activePremiumTenant = t('tenantLink');
  // just double checking the persona for partner and warrantor
  const isPartnerPersona = contextValues?.accessToken?.['W:' + activePremiumTenant] === 'U';
  const isWarrantorPersona = contextValues?.accessToken?.['W:' + activePremiumTenant] === 'S';

  // Determine if user is a partner based on persona checks
  const isPartner = isPartnerPersona && !isWarrantorPersona;

  const categoryColors: Record<string, string> = {
    complaint: 'bg-red-100 text-red-800',
    support: 'bg-blue-100 text-blue-800',
    feedback: 'bg-green-100 text-green-800',
    sales: 'bg-purple-100 text-purple-800',
    technical: 'bg-yellow-100 text-yellow-800',
  };
  const defaultCategoryStyle = 'bg-gray-100 text-gray-800';

  const {
    data: { identity },
  } = useIdentity();

  const { notes, isLoading } = isPartner
    ? useGetNotes({
      tenantLink: activePremiumTenant,
      entityId,
      entityType,
      user_id: contextValues?.accessToken?.user_id,
      isPartner: true,
    }) ?? { notes: [], isLoading: false }
    : useGetNotes({
      tenantLink: activePremiumTenant,
      entityId,
      entityType,
    }) ?? { notes: [], isLoading: false };


  const initialValues = {
    noteType: NoteType.GENERAL,
    title: '',
    content: '',
    date: new Date().getTime(),
    // priority: Priority.STANDARD,
    category: NoteCategoryType.GENERAL,
    status: NoteStatus.ACTIVE,
    entityType: entityType,
    entityId: entityId,
    metadata: undefined,
    attachments: [],
    interactionDate: new Date().getTime(),
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      setButtonLoading(true);

      const warrantor = getRootPersonaFromLexicon(
        t(PrincipalsFieldName),
        PersonaTypeSingleLetter.Warrantor
      );

      const noteData = {
        title: values.title,
        content: values.content,
        interactionDate: new Date(values.date).getTime(),
        category: values.category,
        noteType: values.noteType,
        entityType: values.entityType,
        entityId: values.entityId,
        tenantLink: activePremiumTenant,
        metadata: values.metadata,
        attachments: values.attachments,
        warrantor: warrantor,
        updatedBy: identity.docid,
        updateByName: identity.displayname,
      };

      if (editNote) {
        const response = await nestApiUpdateNote({
          docid: editNote.docid,
          ...noteData,
        });
        if (!response) throw new Error();
        setEditNote(null);
      } else {
        const response = await nestApiCreateNote(noteData);
        if (!response) throw new Error();
        setAddNotes(false);
      }

      setAddNotes(false);
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || error?.message || t('message.somethingWentWrongPleaseTryAgain');
      toast.error(errMsg);
    } finally {
      setButtonLoading(false);
    }
  };

  const NotesTable = CreateTable<DTNote>(notes || [], [
    {
      header: t('general.createdAt'),
      value: (data) => formatUnixTime(data.tcreate, 'dd/MM/yyyy'),
      accessorKey: 'tcreate',
    },
    {
      header: t('general.title'),
      value: (data) => data.title,
      accessorKey: 'title', // Add this
      size: 200,
    },
    {
      header: t('general.category'),
      value: (data) => titleCase(data.category),
      accessorKey: 'category',
    },
    {
      header: t('general.actions'),
      value: (data) => data.docid,
      size: 50,
      Cell: ({ row }: any) => {
        // Find the original note using docid
        const originalNote = notes?.find(
          (note) => note.docid === row.original.actions
        );
        return (
          <div className="grid grid-cols-[40px_40px] gap-2">
            <ButtonIcon
              kind="bgLightGreen"
              Icon={EyeIcon}
              label=""
              iconLeft={true}
              onClick={() => originalNote && setViewNote(originalNote)}
            />
            <ButtonIcon
              kind="bgLightGreen"
              Icon={PencilIcon}
              label=""
              iconLeft={true}
              onClick={() => originalNote && setEditNote(originalNote)}
            />
            <ButtonIcon
              kind="bgRed"
              Icon={TrashIcon}
              label=""
              iconLeft={true}
              onClick={() => originalNote && deleteNote(originalNote)}
            />
          </div>
        );
      },
    },
  ]);

  const deleteNote = async (note: DTNote) => {
    try {
      const response = await nestApiDeleteNote({ docid: note.docid });

      if (response) {
        toast.success(t('Note deleted successfully!'));
      } else {
        toast.error(t('Unable to delete note'));
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error(t('message.somethingWentWrongPleaseTryAgain'));
    }
  };

  const renderMetadataFields = (
    values: typeof initialValues,
    setFieldValue: any
  ) => {
    if (values.noteType === NoteType.INTERACTION) {
      return (
        <div className="space-y-4 rounded-md border border-gray-100 bg-gray-50 p-4">
          <h3 className="font-medium text-gray-700">
            {t('Interaction Details')}
          </h3>

          <Input
            type="select"
            name="metadata.channel"
            label={t('Channel')}
            settings={{
              options: Object.values(InteractionChannel).map((channel) => ({
                label:
                  channel.charAt(0).toUpperCase() +
                  channel.slice(1).toLowerCase(),
                value: channel,
              })),
            }}
          />

          <Input
            type="select"
            name="metadata.party"
            label={t('Interaction With')}
            settings={{
              options: Object.values(InteractionParty).map((party) => ({
                label:
                  party.charAt(0).toUpperCase() + party.slice(1).toLowerCase(),
                value: party,
              })),
            }}
          />

          <Input
            type="select"
            name="metadata.direction"
            label={t('Direction')}
            settings={{
              options: Object.values(CommunicationDirection).map((dir) => ({
                label: dir.charAt(0).toUpperCase() + dir.slice(1).toLowerCase(),
                value: dir,
              })),
            }}
          />

          <Input
            type="text"
            name="metadata.contactInfo"
            label={t('Contact Information')}
            settings={{
              placeholder: 'Name, email, phone, etc.',
            }}
          />

          {/* Channel-specific fields */}
          {values.noteType === NoteType.INTERACTION && values.metadata && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="time"
                name="metadata.duration"
                label={t('Duration')}
                settings={{ type: 'time', placeholder: 'hh:mm' }}
              />
              <Input
                type="phone"
                name="metadata.phoneNumber"
                label={t('Phone Number')}
                settings={{
                  placeholder: 'Phone number',
                }}
              />
            </div>
          )}

          {values.metadata &&
            (values.metadata as InteractionMetadata)?.channel ===
            InteractionChannel.EMAIL && (
              <>
                <Input
                  type="email"
                  name="metadata.subject"
                  label={t('Subject Line')}
                  settings={{
                    placeholder: 'Subject line',
                  }}
                />
                <Input
                  type="text"
                  name="metadata.recipients"
                  label={t('Recipients')}
                  settings={{
                    placeholder: 'email@example.com, email2@example.com',
                  }}
                />
              </>
            )}

          <Input
            type="checkbox"
            name="metadata.followUpRequired"
            label={t('Follow-up Required')}
            settings={{}}
          />

          {values.metadata &&
            (values.metadata as InteractionMetadata)?.followUpRequired && (
              <Input
                type="datepicker"
                name="metadata.followUpDate"
                label={t('Follow-up Date')}
                settings={{
                  placeholder: 'Follow-up Date',
                }}
              />
            )}
        </div>
      );
    }
    return null;
  };

  const NoteForm = ({ isEdit = false, onSubmit, initialData }: any) => (
    <Formik
      initialValues={initialData}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, setFieldValue, values }) => (
        <Form className="flex flex-col gap-6 p-6">
          {/* <Input
            type="datepicker"
            name="date"
            label={t('Date')}
            settings={{
              placeholder: 'DD/MM/YYYY',
            }}
          /> */}

          <Input
            type="text"
            name="title"
            label={t('Title')}
            settings={{
              placeholder: t('Enter note title'),
              hint: '*required',
            }}
          />

          <Input
            type="rich-text"
            name="content"
            label={t('Description')}
            settings={{
              placeholder: t('Enter note description'),
              hint: '*required',
            }}
          />

          <Input
            type="select"
            name="category"
            label={t('Category')}
            settings={{
              options: categoryOptions,
            }}
          />

          <Input
            type="file"
            name="attachments"
            label="Attachments"
            settings={{
              accepts: ['image/*', '.pdf'],
              autoUpload: true,
              multiple: true,
            }}
          />

          <div className="flex gap-3 pt-2">
            <Button
              kind="primary"
              type="submit"
              disabled={isSubmitting}
              loading={buttonLoading}
            >
              {t('Save')}
            </Button>
            <Button
              kind="outline_red"
              onClick={() => {
                if (isEdit) {
                  setEditNote(null);
                } else {
                  setAddNotes(false);
                }
              }}
            >
              {t('Cancel')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );

  return (
    <>
      <CardWrapperWithHeader
        title={t('Notes')}
        headerRight={
          <Button
            icon={<PlusIcon />}
            kind="primary"
            size={'sm'}
            onClick={() => setAddNotes(true)}
          >
            {t('Add note')}
          </Button>
        }
      >
        <div className="max-h-[300px] overflow-y-auto">
          <NotesTable
            initialState={{
              showGlobalFilter: true,
              sorting: [{ id: 'actions', desc: true }],
            }}
            muiSearchTextFieldProps={{
              placeholder: t('general.searchByIDOrProductName'),
            }}
            state={{
              isLoading: false,
            }}
            enablePagination={false}
          />
        </div>
      </CardWrapperWithHeader>

      {/* View Note Modal */}
      {viewNote && (
        <Modal
          kind="generic"
          settings={{
            title: t('View Note'),
            handleClose: () => setViewNote(null),
          }}
          className="w-[600px] rounded-md bg-white"
        >
          <div className="flex flex-col gap-4 p-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">{t('Date')}</p>
                <p className="mt-1 text-base">
                  {formatUnixTime(viewNote.interactionDate, 'dd/MM/yyyy')}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">{t('Title')}</p>
                <p className="mt-1 text-lg font-medium">{viewNote.title}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">{t('Category')}</p>
                <div className="mt-1">
                  <span
                    className={`inline-flex rounded-md px-3 py-1 text-sm font-medium ${categoryColors[viewNote.category] ?? defaultCategoryStyle}`}
                  >
                    {viewNote.category.charAt(0).toUpperCase() +
                      viewNote.category.slice(1)}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">{t('Description')}</p>
                <div className="mt-1 rounded-md bg-gray-50 p-4">
                  <div dangerouslySetInnerHTML={{ __html: viewNote.content }} />
                </div>
              </div>

              {viewNote.attachments && viewNote.attachments.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500">{t('Attachments')}</p>
                  <div className="mt-1 space-y-2">
                    {viewNote.attachments.map((file) => (
                      <a
                        key={file.ogFilename}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:underline"
                      >
                        <DocumentIcon className="h-4 w-4" />
                        {file.metadata?.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="border-t pt-4 text-sm text-gray-500">
              {`${t('Created by')}: ${viewNote?.updateByName || ''} | 
              ${t('Created at')}: ${formatUnixTime(
                viewNote.tcreate,
                'dd/MM/yyyy'
              )} | 
              ${t('Modified at')}: ${formatUnixTime(
                viewNote.tupdate || viewNote.tcreate,
                'dd/MM/yyyy HH:mm a'
              )}`}
            </div>

            <div className="mt-4 flex justify-end gap-3 border-t pt-4">
              <Button
                kind="outline"
                onClick={() => {
                  setViewNote(null);
                  setEditNote(viewNote);
                }}
              >
                {t('Edit')}
              </Button>
              <Button kind="outline_red" onClick={() => setViewNote(null)}>
                {t('Close')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Note Modal */}
      {(addNotes || editNote) && (
        <Modal
          kind="generic"
          settings={{
            title: editNote ? t('Edit Note') : t('Add Note'),
            handleClose: () => setAddNotes(false),
          }}
          className="w-[600px] rounded-md bg-white"
        >
          <NoteForm
            isEdit={!!editNote}
            onSubmit={onSubmit}
            initialData={
              editNote ? { ...editNote, date: editNote.tcreate } : initialValues
            }
          />
        </Modal>
      )}
    </>
  );
}
